import { useParams, useSearchParams } from 'react-router-dom';
import {
  CurrencyCode,
  PaymentStatus,
  UserRole,
  formatMoney,
  isDistributorRole,
  type Payment,
  type UserRole as UserRoleType,
} from '@lombok/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { AccountName } from '@/components/AccountName';
import { PageHeader } from '@/components/PageHeader';
import { FORM_COLUMN } from '@/components/layout';
import { BackLink, DetailRow } from '@/components/DetailRow';
import { StatusPill } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { useInvoice, useOrder, usePayment } from '@/data/hooks';
import { orderLabel, paymentLabel } from '@/lib/names';
import { invoiceNumberLabel } from '@/features/invoices/labels';
import { paymentMethodLabel } from './labels';
import { SubaccountName } from './SubaccountName';
import { PaymentDecisionActions } from './PaymentDecisionActions';
import { PaymentAllocatePanel } from './PaymentAllocatePanel';

/** Roles that may approve/reject payments (mirrors backend — UX gate only). */
const REVIEW_ROLES: readonly UserRoleType[] = [
  UserRole.SUPER_ADMIN,
  UserRole.ADMIN,
  UserRole.FINANCE,
];

interface PaymentDetailPageProps {
  /** Group root for the "Pagos" back link, e.g. "/app/customer/pagos". */
  basePath: string;
  backLabel?: string;
}

/**
 * Pago detail: amounts (the payment's own currency + USD/VES equivalents from
 * the recorded rate), method, reference, related order/invoice, proof status,
 * and the review outcome (reviewer + notes). For reviewer roles a PENDING
 * payment shows the Aprobar/Rechazar actions. The backend is the authority on
 * every decision and on which fields a role may see.
 */
export function PaymentDetailPage({ basePath, backLabel = 'Pagos' }: PaymentDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // accountId identifies the payment's DynamoDB partition; the list rows pass it in the URL.
  const accountId = searchParams.get('accountId') ?? undefined;
  const query = usePayment(id, accountId);
  const payment = query.data;
  const { profile } = useSessionProfile();
  const isReviewer = profile ? REVIEW_ROLES.includes(profile.role) : false;

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={basePath}>{backLabel}</BackLink>
        <PageHeader
          title={payment ? `Pago ${paymentLabel(payment)}` : 'Pago'}
          description="Detalle del pago y su estado de revisión."
          actions={payment ? <StatusPill kind="payment" value={payment.status} /> : undefined}
        />
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Cargando pago…"
      >
        {payment ? (
          <div className="flex flex-col gap-6">
            {isReviewer && payment.status === PaymentStatus.PENDING ? (
              <Card>
                <CardHeader>
                  <CardTitle>Revisión</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <PaymentDecisionActions paymentId={payment.paymentId} accountId={payment.accountId} />
                </CardContent>
              </Card>
            ) : null}

            <AmountCard payment={payment} />
            <DetailsCard payment={payment} />

            {/* Finance/admin can apply an approved customer payment to open invoices. */}
            {isReviewer &&
            payment.status === PaymentStatus.APPROVED &&
            payment.accountType === 'customer' ? (
              <PaymentAllocatePanel payment={payment} />
            ) : null}
          </div>
        ) : null}
      </QueryState>
    </div>
  );
}

/** Amounts panel: the payment's own currency + the USD/VES equivalents. */
function AmountCard({ payment: p }: { payment: Payment }) {
  const isVes = p.currency === CurrencyCode.VES;
  const hasRate = Boolean(p.exchangeRate && p.exchangeRate > 0);

  // Show both sides when a rate is recorded; otherwise just the native amount.
  const usdMinor = isVes
    ? hasRate
      ? Math.round(p.amountMinor / (p.exchangeRate as number))
      : undefined
    : p.amountMinor;
  const vesMinor = isVes
    ? p.amountMinor
    : hasRate
      ? Math.round(p.amountMinor * (p.exchangeRate as number))
      : undefined;

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-muted">Monto (USD)</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {usdMinor != null ? formatMoney(usdMinor, CurrencyCode.USD) : '—'}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-muted">Monto (VES)</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {vesMinor != null ? formatMoney(vesMinor, CurrencyCode.VES) : '—'}
          </p>
          {hasRate ? (
            <p className="mt-1 text-xs text-text-muted">Tasa registrada: {p.exchangeRate} VES/USD</p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

/** Method, reference, links, proof status, and the review outcome. */
function DetailsCard({ payment: p }: { payment: Payment }) {
  const { profile } = useSessionProfile();
  const isDistributor = profile ? isDistributorRole(profile.role) : false;
  const accountTypeLabel = isDistributor ? 'Subcliente' : p.accountType === 'distributor' ? 'Distribuidor' : 'Cliente';
  return (
    <Card>
      <CardHeader>
        <CardTitle>Información del pago</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="flex flex-col">
          <DetailRow label="Estado">
            <StatusPill kind="payment" value={p.status} />
          </DetailRow>
          <DetailRow label="Método">{paymentMethodLabel(p.method)}</DetailRow>
          <DetailRow label="Moneda">{p.currency}</DetailRow>
          <DetailRow label={accountTypeLabel}>
            {isDistributor ? <SubaccountName payment={p} /> : <AccountName id={p.accountId} />}
          </DetailRow>
          <DetailRow label="Número de referencia">{p.referenceNumber ?? '—'}</DetailRow>
          <DetailRow label="Pedido relacionado">
            <RelatedOrder orderId={p.relatedOrderId} />
          </DetailRow>
          {p.relatedInvoiceId ? (
            <DetailRow label="Factura relacionada">
              <RelatedInvoice invoiceId={p.relatedInvoiceId} customerId={p.accountType === 'customer' ? p.accountId : undefined} />
            </DetailRow>
          ) : null}
          <DetailRow label="Comprobante">
            {p.proofS3Key ? (
              <span className="inline-flex items-center gap-1.5 text-success">
                <CheckIcon />
                Adjunto
              </span>
            ) : (
              <span className="text-text-muted">Sin comprobante</span>
            )}
          </DetailRow>
          <DetailRow label="Registrado">{formatDateTime(p.createdAt)}</DetailRow>
          {p.reviewedAt ? (
            <DetailRow label="Revisado">{formatDateTime(p.reviewedAt)}</DetailRow>
          ) : null}
          {p.reviewedByName ? <DetailRow label="Revisado por">{p.reviewedByName}</DetailRow> : null}
          {p.notes ? <DetailRow label="Notas">{p.notes}</DetailRow> : null}
        </dl>
      </CardContent>
    </Card>
  );
}

/** The related order by its number (PED-…); while it loads, or if it cannot be read, a plain word, never the id. */
function RelatedOrder({ orderId }: { orderId?: string }) {
  const order = useOrder(orderId);
  if (!orderId) return <>—</>;
  if (order.data) return <>{orderLabel(order.data.order)}</>;
  return <>{order.isLoading ? '…' : 'Pedido asociado'}</>;
}

/** The related invoice by its number; only customer accounts have invoices to look up. */
function RelatedInvoice({ invoiceId, customerId }: { invoiceId: string; customerId?: string }) {
  const invoice = useInvoice(invoiceId, customerId);
  if (invoice.data) return <>{invoiceNumberLabel(invoice.data)}</>;
  return <>{invoice.isLoading ? '…' : 'Factura asociada'}</>;
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m20 6-11 11-5-5" />
    </svg>
  );
}

/** Local date-time (es-VE); raw value fallback if unparseable. */
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-VE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}
